import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { AlertCircle, CheckCircle2, ChevronRight, Clock } from 'lucide-react'

interface HitlResolutionCardProps {
  pending: boolean
  reasonBadge?: React.ReactNode
  reasonLabel?: string
  previewText?: string | null
  feedback?: string | null
  error?: string | null
  onOpenModal: () => void
}

export function HitlResolutionCard({
  pending,
  reasonBadge,
  reasonLabel,
  previewText,
  feedback,
  error,
  onOpenModal,
}: HitlResolutionCardProps) {
  if (!pending && !feedback && !error) return null

  return (
    <div className="mb-3 space-y-2">
      <AnimatePresence mode="wait">
        {pending && (
          <motion.div
            key="hitl-pending-card"
            role="region"
            aria-label="Pausa HITL"
            initial={{ opacity: 0, y: -10, scale: 0.98 }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
              boxShadow: [
                '0 2px 8px -2px rgba(249, 115, 22, 0.12)',
                '0 6px 20px 2px rgba(249, 115, 22, 0.28)',
                '0 2px 8px -2px rgba(249, 115, 22, 0.12)',
              ],
              borderColor: [
                'rgba(251, 146, 60, 0.45)',
                'rgba(249, 115, 22, 0.9)',
                'rgba(251, 146, 60, 0.45)',
              ],
            }}
            exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
            transition={{
              boxShadow: { duration: 2.4, repeat: Infinity, ease: 'easeInOut' },
              borderColor: { duration: 2.4, repeat: Infinity, ease: 'easeInOut' },
              default: { duration: 0.25 },
            }}
            className="rounded-xl border bg-gradient-to-r from-orange-50 via-amber-50 to-orange-50 p-3.5 shadow-xs"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="relative flex items-center justify-center">
                  <motion.span
                    className="absolute -inset-1 rounded-full bg-orange-400 opacity-40 blur-xs"
                    animate={{ scale: [1, 1.4, 1], opacity: [0.3, 0.65, 0.3] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                  />
                  <span className="relative flex h-6 w-6 items-center justify-center rounded-full bg-orange-100 text-orange-600 border border-orange-200">
                    <AlertCircle className="h-4 w-4" />
                  </span>
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <p className="text-xs font-bold text-orange-900">
                      Pausa HITL — Revisión humana requerida
                    </p>
                    <motion.span
                      className="inline-block w-2 h-2 rounded-full bg-amber-500"
                      animate={{ opacity: [1, 0.3, 1] }}
                      transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
                      title="En espera de atención"
                    />
                  </div>
                </div>
              </div>
              {reasonBadge}
            </div>

            <p className="mt-2 text-xs leading-relaxed text-orange-800">
              {previewText ||
                'La conversación se encuentra pausada a la espera de confirmación humana para avanzar con checkout o cotización.'}
            </p>

            {reasonLabel && (
              <p className="mt-1 text-[11px] font-medium text-orange-700 flex items-center gap-1">
                <Clock className="w-3 h-3 text-orange-500" />
                <span>Causa: <strong>{reasonLabel}</strong></span>
              </p>
            )}

            <div className="mt-2.5 flex items-center justify-end">
              <motion.button
                type="button"
                onClick={onOpenModal}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                animate={{
                  boxShadow: [
                    '0 1px 3px 0 rgba(234, 88, 12, 0.3)',
                    '0 3px 12px 2px rgba(234, 88, 12, 0.45)',
                    '0 1px 3px 0 rgba(234, 88, 12, 0.3)',
                  ],
                }}
                transition={{
                  boxShadow: { duration: 1.8, repeat: Infinity, ease: 'easeInOut' },
                }}
                className="inline-flex items-center gap-1.5 rounded-lg bg-orange-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs transition-colors hover:bg-orange-700 cursor-pointer"
              >
                <span>Resolver pausa HITL</span>
                <ChevronRight className="h-3.5 w-3.5" />
              </motion.button>
            </div>
          </motion.div>
        )}

        {!pending && feedback && (
          <motion.div
            key="hitl-feedback"
            role="status"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-800"
          >
            <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-emerald-600" />
            <span>{feedback}</span>
          </motion.div>
        )}

        {!pending && error && (
          <motion.div
            key="hitl-error"
            role="alert"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-800"
          >
            <AlertCircle className="h-4 w-4 flex-shrink-0 text-rose-600" />
            <span>{error}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
